<template>
  <div>
    <v-data-table
      :headers="computedHeaders"
      :items="visiblePrograms"
      :items-per-page="table.itemsPerPage"
      :footer-props="table.footerProps"
      :options.sync="table.options"
      :server-items-length="table.total"
      :show-expand="showExpand"
      :expanded="expandedPrograms"
      :loading="table.loading"
      loading-text="loading"
      no-data-text="No programs available"
      no-results-text="No programs found"
      item-key="programId"
      id="program-table"
      @click:row="handleRowClick"
    >
      <template v-slot:expanded-item="{ item, headers }">
        <ProgramAdminExpansion
          v-if="showExpand && item && item.clientId != 0"
          :item="item"
          :headers="headers"
          :budgets="budgets"
          :display-loading-icon-on-program="displayLoadingIconOnProgram"
          @message-program-audience="
            (item, audience) => $emit('message-program-audience', item, audience)
          "
          @load-program-wizard="
            (item, isEdit, step) => $emit('load-program-wizard', item, isEdit, step)
          "
          @delete-program-confirmation="(item) => $emit('delete-program-confirmation', item)"
          @load-metric-ui="(programId) => $emit('load-metric-ui', programId)"
        />
      </template>
      <template v-slot:[`item.actionItems`]="{ item }">
        <div
          class="d-flex justify-space-between align-center"
          :class="$vuetify.breakpoint.xs && userProfile.clientId != 1 ? 'flex-column' : ''"
          :style="$vuetify.breakpoint.xs && userProfile.clientId == 1 ? { width: '40%' } : {}"
        >
          <!-- Program action buttons -->
          <v-tooltip top open-delay="200">
            <template v-slot:activator="{ on, attrs }">
              <div class="d-flex align-center py-1" v-if="userProfile.clientId == 1">
                <v-icon
                  color="brandCyan"
                  v-if="item.status != 'Deleted'"
                  @click="$emit('load-program-wizard', item, true)"
                  v-on="on"
                  v-bind="attrs"
                  >mdi-pencil</v-icon
                >
              </div>
            </template>
            <span>Edit Program</span>
          </v-tooltip>
          <!-- If Whistle, they'll see just icons with tooltips -->
          <!-- Other clients will just see duplicate icon with no tooltip but description -->
          <v-tooltip top :disabled="userProfile.clientId != 1" open-delay="200">
            <template v-slot:activator="{ on, attrs }">
              <div class="d-flex align-center py-1">
                <v-icon
                  color="brandCyan"
                  @click="$emit('load-program-wizard', item, false)"
                  v-on="on"
                  v-bind="attrs"
                  >mdi-content-copy</v-icon
                >
                <p class="mb-0 mr-3 pl-2" v-if="userProfile.clientId != 1">Duplicate Program</p>
              </div>
            </template>
            <span>Duplicate Program</span>
          </v-tooltip>
          <v-tooltip top :disabled="userProfile.clientId != 1" open-delay="200">
            <template v-slot:activator="{ on, attrs }">
              <div class="d-flex align-center py-1" v-if="userProfile.clientId == 1">
                <v-icon
                  color="brandCyan"
                  :disabled="
                    item.status == 'Active' ||
                    item.status == 'Archived' ||
                    !permissions.includes('programs:delete:program') ||
                    (item.clientId != clientId && userProfile.clientId != 1)
                  "
                  @click="$emit('delete-program-confirmation', item)"
                  :loading="item.programId == displayLoadingIconOnProgram"
                  v-on="on"
                  v-bind="attrs"
                  >mdi-trash-can-outline</v-icon
                >
              </div>
            </template>
            <span>Delete Program</span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
        <v-btn
          @click.stop="
            expand(!isExpanded);
            $emit('get-budget', item);
          "
          v-if="item.clientId != 0"
          icon
          ><v-icon large>{{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon></v-btn
        >
      </template>
      <template v-slot:loading>
        <v-progress-circular
          indeterminate
          color="primary"
          class="my-8"
          :size="50"
          :width="4"
        ></v-progress-circular>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapState } from "vuex";
import ProgramAdminExpansion from "./ProgramAdminExpansion.vue";

export default {
  name: "ProgramAdminTable",
  components: {
    ProgramAdminExpansion,
  },
  props: {
    showExpand: {
      type: Boolean,
      default: false,
    },
    programs: {
      type: Array,
      default: () => [],
    },
    table: {
      type: Object,
      required: true,
    },
    expandedPrograms: {
      type: Array,
      default: () => [],
    },
    budgets: {
      type: Object,
      default: () => ({}),
    },
    displayLoadingIconOnProgram: {
      type: [String, Number],
      default: null,
    },
  },
  computed: {
    ...mapState(["userProfile", "permissions", "globalClientId"]),
    clientId() {
      return this.globalClientId;
    },
    visiblePrograms() {
      if (this.table.loading) return [];
      return this.table.programs || this.programs || [];
    },
    computedHeaders() {
      if (this.showExpand) {
        return [
          {
            text: "Id",
            value: "programId",
            width: "10%",
            align:
              this.permissions && this.permissions.includes("admin:manage:clients")
                ? "start"
                : " d-none",
          },
          {
            text: "Program Name",
            value: "displayName",
            width: "50%",
          },
          {
            text: "Status",
            value: "status",
            width: "40%",
          },
          { text: "Details", value: "data-table-expand", width: "10%" },
        ];
      } else {
        return [
          {
            text: "Program Name",
            value: "displayName",
            width: "50%",
          },
          {
            text: "",
            value: "actionItems",
            width: "50%",
          },
        ];
      }
    },
  },
  methods: {
    handleRowClick(value) {
      console.log("Row clicked:", value);
      this.$emit("get-budget", value);

      //add or remove the item from expanded array
      const index = this.expandedPrograms.indexOf(value);
      console.log("INDEX ", index);
      if (index === -1) {
        this.$emit("expand-program", value);
      } else {
        this.$emit("collapse-program", value);
      }
    },
  },
};
</script>

<style scoped>
.program-action-button {
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.5;
  font-size: 16px;
  justify-content: flex-start;
  padding-left: 0 !important;
}

.program-action-button p {
  margin-left: 8px;
}

/* Removes box shadow from program table expansion */
#program-table >>> .v-data-table__expanded__content {
  box-shadow: none;
}

/* Decreases padding in the table's expanded content */
#program-table-inner >>> .col {
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Adds padding to the right of inner table icons */
#program-table-inner >>> .v-icon {
  padding-right: 6px;
}
</style>
